/**
 * Wave 4 — Programmatic SEO batch generation console.
 *
 * Route: `/app/landing-pages/programmatic`
 *
 * Layout: left-hand form (template + topic_pattern + region list) →
 * right-hand batch list, click through to a detail modal showing status,
 * generated pages, per-region failures, cost/latency footprints.
 *
 * The backend `POST /api/v1/lp-v2/programmatic/generate-batch` runs
 * synchronously (per-region), so we don't strictly need polling — but we
 * still poll `GET /programmatic/batches/{id}` for robustness (mirrors the
 * research route pattern) and to reveal any partial failures.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Layers, Play, RefreshCw, Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const BRIMO_REGIONS = [
  "Wuppertal",
  "Bonn",
  "Leverkusen",
  "Kreis Mettmann",
  "Kreis Neuss",
  "Münster",
  "Bochum",
];

function BatchStatusBadge({ status }) {
  const tone =
    status === "succeeded"
      ? "bg-emerald-100 text-emerald-900"
      : status === "partial"
      ? "bg-amber-100 text-amber-900"
      : status === "failed"
      ? "bg-rose-100 text-rose-900"
      : status === "generating"
      ? "bg-sky-100 text-sky-900"
      : "bg-stone-100 text-stone-900";
  return (
    <Badge
      className={`text-[11px] ${tone}`}
      data-testid={`pseo-batch-status-${status}`}
    >
      {status}
    </Badge>
  );
}

export default function ProgrammaticSEO() {
  const t = useT();
  const nav = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [topic, setTopic] = useState("Immobilienbewertung");
  const [regionsText, setRegionsText] = useState(BRIMO_REGIONS.join("\n"));
  const [batches, setBatches] = useState([]);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openBatchId, setOpenBatchId] = useState(null);
  const [batchDetail, setBatchDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tpls, bs] = await Promise.all([
        api.get("/lp-v2/templates"),
        api.get("/lp-v2/programmatic/batches"),
      ]);
      setTemplates(tpls.data.items || []);
      setBatches(bs.data.items || []);
      if (!templateId && tpls.data.items?.length) {
        setTemplateId(tpls.data.items[0].id);
      }
    } catch (e) {
      /* noop */
    } finally {
      setLoading(false);
    }
  }, [templateId]);
  useEffect(() => {
    load();
  }, [load]);

  // Poll the currently-open batch detail (research-route pattern).
  useEffect(() => {
    if (!openBatchId) return undefined;
    let alive = true;
    let timer = null;
    const tick = async () => {
      try {
        const r = await api.get(`/lp-v2/programmatic/batches/${openBatchId}`);
        if (!alive) return;
        setBatchDetail(r.data);
        if (["succeeded", "failed", "partial"].includes(r.data?.status)) return;
      } catch (e) {
        /* keep polling */
      }
      timer = setTimeout(tick, 2000);
    };
    tick();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [openBatchId]);

  const regions = useMemo(
    () =>
      regionsText
        .split("\n")
        .map((r) => r.trim())
        .filter(Boolean),
    [regionsText],
  );

  async function runBatch() {
    if (!templateId) {
      toast.error(t("programmatic_seo.tpl_required", "Template auswählen."));
      return;
    }
    if (regions.length === 0) {
      toast.error(t("programmatic_seo.regions_required", "Mindestens eine Region angeben."));
      return;
    }
    setCreating(true);
    try {
      const r = await api.post("/lp-v2/programmatic/generate-batch", {
        template_id: templateId,
        topic_pattern: topic,
        region_list: regions,
        page_count_limit: 50,
      });
      toast.success(
        t(
          "programmatic_seo.batch_ok",
          "Batch abgeschlossen: {{n}} Seiten generiert.",
        ).replace("{{n}}", String(r.data.succeeded_pages || 0)),
      );
      setOpenBatchId(r.data.id);
      load();
    } catch (e) {
      toast.error(t("programmatic_seo.batch_failed", "Batch-Erstellung fehlgeschlagen."));
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="space-y-4" data-testid="pseo-page">
      <header className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => nav("/app/landing-pages")}
            data-testid="pseo-back-btn"
          >
            <ArrowLeft className="mr-1 size-4" />
            {t("programmatic_seo.back_to_lp", "Zurück zu Landing Pages")}
          </Button>
          <h1 className="text-2xl font-semibold mt-1" data-testid="pseo-title">
            <Layers className="inline mr-1 size-6" />
            {t("programmatic_seo.title", "Programmatische SEO")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "programmatic_seo.subtitle",
              "Regionale Landing-Page-Fanouts aus Templates + Skill-Enrichment.",
            )}
          </p>
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-[420px_1fr]">
        {/* ─── Batch form ─────────────────────────────────────── */}
        <Card data-testid="pseo-form-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              {t("programmatic_seo.form_title", "Neuer Batch")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">
                {t("programmatic_seo.template", "Template")}
              </label>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger data-testid="pseo-template-select">
                  <SelectValue placeholder="Template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((tpl) => (
                    <SelectItem
                      key={tpl.id}
                      value={tpl.id}
                      data-testid={`pseo-template-opt-${tpl.slug}`}
                    >
                      {tpl.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">
                {t("programmatic_seo.topic", "Topic-Pattern")}
              </label>
              <Input
                data-testid="pseo-topic-input"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Immobilienbewertung"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">
                {t("programmatic_seo.regions", "Regionen (eine pro Zeile)")}
              </label>
              <Textarea
                data-testid="pseo-regions-input"
                rows={7}
                value={regionsText}
                onChange={(e) => setRegionsText(e.target.value)}
                className="font-mono text-xs"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                {t(
                  "programmatic_seo.regions_hint",
                  "{{n}} Regionen · max. 50 pro Batch",
                ).replace("{{n}}", String(regions.length))}
              </p>
            </div>
            <Button
              data-testid="pseo-run-batch-btn"
              onClick={runBatch}
              disabled={creating || !templateId}
              className="w-full"
            >
              <Play className="mr-1 size-4" />
              {creating
                ? t("programmatic_seo.generating", "Generiere…")
                : t("programmatic_seo.run_batch", "Batch starten")}
            </Button>
          </CardContent>
        </Card>

        {/* ─── Batch list ─────────────────────────────────────── */}
        <Card data-testid="pseo-batches-card">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm">
              {t("programmatic_seo.batches", "Batches")}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={load}
              data-testid="pseo-refresh-btn"
              aria-label="refresh"
            >
              <RefreshCw className="size-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground" data-testid="pseo-loading">
                {t("common.loading", "Lade…")}
              </p>
            ) : batches.length === 0 ? (
              <p
                className="text-sm text-muted-foreground"
                data-testid="pseo-batches-empty"
              >
                {t(
                  "programmatic_seo.batches_empty",
                  "Noch keine Batches. Starten Sie den ersten links.",
                )}
              </p>
            ) : (
              <ul className="divide-y" data-testid="pseo-batches-list">
                {batches.map((b) => (
                  <li
                    key={b.id}
                    className="py-2 flex items-center gap-3"
                    data-testid={`pseo-batch-row-${b.id}`}
                  >
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium truncate">
                          {b.generation_input?.topic_pattern || b.template_id.slice(0, 8)}
                        </span>
                        <BatchStatusBadge status={b.status} />
                        <Badge variant="outline" className="text-[11px] font-mono">
                          {b.succeeded_pages}/{b.total_pages} ok
                        </Badge>
                        {b.failed_pages > 0 && (
                          <Badge className="bg-rose-100 text-rose-900 text-[11px]">
                            {b.failed_pages} failed
                          </Badge>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground font-mono truncate">
                        {b.created_at?.slice(0, 19)?.replace("T", " ")} ·{" "}
                        {(b.total_latency_ms / 1000).toFixed(1)}s · $
                        {(b.total_cost_usd || 0).toFixed(4)}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setOpenBatchId(b.id)}
                      data-testid={`pseo-batch-open-${b.id}`}
                    >
                      {t("programmatic_seo.open", "Öffnen")}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <BatchDetailDialog
        open={!!openBatchId}
        onOpenChange={(o) => {
          if (!o) {
            setOpenBatchId(null);
            setBatchDetail(null);
          }
        }}
        batch={batchDetail}
      />
    </div>
  );
}

function BatchDetailDialog({ open, onOpenChange, batch }) {
  const t = useT();
  const [pages, setPages] = useState([]);
  useEffect(() => {
    if (!batch) return;
    let alive = true;
    (async () => {
      const rows = [];
      for (const pid of (batch.generated_page_ids || []).slice(0, 20)) {
        try {
          const r = await api.get(`/lp-v2/${pid}`);
          rows.push(r.data);
        } catch (e) {
          /* skip */
        }
      }
      if (alive) setPages(rows);
    })();
    return () => {
      alive = false;
    };
  }, [batch]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="pseo-batch-detail"
        className="max-w-3xl max-h-[80vh] overflow-auto"
      >
        <DialogHeader>
          <DialogTitle>
            <Sparkles className="inline mr-1 size-4" />
            {batch?.generation_input?.topic_pattern || "Batch"}{" "}
            {batch?.status && <BatchStatusBadge status={batch.status} />}
          </DialogTitle>
        </DialogHeader>
        {!batch ? (
          <p className="text-sm text-muted-foreground">
            {t("common.loading", "Lade…")}
          </p>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <div>
                <div className="text-muted-foreground">Total</div>
                <div className="font-mono" data-testid="pseo-detail-total">
                  {batch.total_pages}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Succeeded</div>
                <div
                  className="font-mono text-emerald-700"
                  data-testid="pseo-detail-succeeded"
                >
                  {batch.succeeded_pages}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Failed</div>
                <div className="font-mono text-rose-700" data-testid="pseo-detail-failed">
                  {batch.failed_pages}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Cost</div>
                <div className="font-mono" data-testid="pseo-detail-cost">
                  ${(batch.total_cost_usd || 0).toFixed(4)}
                </div>
              </div>
            </div>
            {batch.failed_regions?.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground">Failed regions</p>
                <div className="flex flex-wrap gap-1" data-testid="pseo-failed-regions">
                  {batch.failed_regions.map((r) => (
                    <Badge
                      key={r}
                      className="bg-rose-100 text-rose-900 text-[11px]"
                    >
                      {r}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                {t("programmatic_seo.generated_pages", "Generierte Seiten")}
              </p>
              <ul className="divide-y" data-testid="pseo-generated-list">
                {pages.map((p) => (
                  <li
                    key={p.id}
                    className="py-2 flex items-center gap-2"
                    data-testid={`pseo-generated-row-${p.id}`}
                  >
                    <span className="text-sm font-medium flex-1 truncate">
                      {p.title || p.slug}
                    </span>
                    {p.region && (
                      <Badge variant="outline" className="text-[11px]">
                        {p.region}
                      </Badge>
                    )}
                    <Link
                      to={`/app/landing-pages/${p.id}`}
                      className="text-xs text-sky-700 hover:underline"
                      data-testid={`pseo-generated-open-${p.id}`}
                    >
                      {t("programmatic_seo.open", "Öffnen")}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
